import axios, { AxiosRequestConfig } from "axios";
import { CustomTokenhUrl, AuthenticateServer } from './api';
import { observable, autorun } from 'mobx';
import Firebase from '../firebase'
export const CUSTOM_TOKEN_KEY = 'customToken'
export const COGNITO_JWT = 'cognitoJwt'
const CUSTOM_TOKEN_URI = CustomTokenhUrl()

const AWS= require('aws-sdk')
const AmazonCognitoIdentity = require('amazon-cognito-identity-js');

const validate = (val:string|null) => !val ? null : val === "null" ? null : val
export const sessionObservable = observable({
    customToken: validate(localStorage.getItem(CUSTOM_TOKEN_KEY)),
    cognitoJwt: validate(localStorage.getItem(COGNITO_JWT))
})

autorun(() => {
    localStorage.setItem(CUSTOM_TOKEN_KEY, sessionObservable.customToken!)
    localStorage.setItem(COGNITO_JWT, sessionObservable.cognitoJwt!)
})

const poolData = {
    UserPoolId: AuthenticateServer.UserPoolId,
    ClientId: AuthenticateServer.ClientId,
    // Use localStorage so the Cognito session (incl. refresh token) survives a browser/tab
    // restart and is shared across tabs — matching where `cognitoJwt` is persisted (localStorage).
    // With sessionStorage they had different lifetimes: a new tab/restart kept `cognitoJwt`
    // but lost the refresh token, so the first 401 forced a logout even though the refresh
    // token was still valid.
    Storage: localStorage
};
const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData);
// Amazon Cognito 認証情報プロバイダーを初期化します
AWS.config.region = AuthenticateServer.Region; // リージョン
AWS.config.credentials = new AWS.CognitoIdentityCredentials({
    IdentityPoolId: AuthenticateServer.IdentityPoolId,
});

export const cognitoLogin = async(param: {
    username:string,
    password:string
}) => {
    // 認証データの作成
    const authenticationData = {
        Username: param.username,
        Password: param.password
    };
    const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(
        authenticationData
    );

    const userData = {
        Username: param.username,
        Pool: userPool,
        // Keep in sync with poolData.Storage (localStorage) so the session persists across restarts.
        Storage: localStorage
    };
    const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

    // 認証処理
    let p =  new Promise((resolve, reject) => {
        cognitoUser.authenticateUser(authenticationDetails, {
            onSuccess: result => {
                const idToken = result.getIdToken().getJwtToken(); // IDトークン

                sessionObservable.cognitoJwt = idToken
                let r = {
                    cognitoJwt:idToken
                }
                resolve(r)
            },
            onFailure: err => {
                reject(err)
            }
        });
    });
    return await p;
}

export const logout = () => {
    sessionObservable.customToken = null
    sessionObservable.cognitoJwt = null
    // Cognito session (incl. refresh token) now lives in localStorage; signOut() clears its keys.
    const cognitoUser = userPool.getCurrentUser()
    if (cognitoUser) {
        cognitoUser.signOut()
    }
    sessionStorage.clear();
}


export const authorizedFetch = (() => {
    const instance = axios.create()

    // REQUEST
    instance.interceptors.request.use(
        (config) => {
            if (sessionObservable.cognitoJwt) {
                const headers: Record<string, string> = {
                    "XXX-Authorize": sessionObservable.cognitoJwt
                }
                // Don't set Content-Type for FormData - let axios handle it automatically
                // so that multipart/form-data boundary is set correctly for file uploads
                if (!(config.data instanceof FormData)) {
                    headers["Content-Type"] = "application/json"
                }
                config.headers = Object.assign(config.headers || {}, headers)
            }
            return config;
        },
        error => {
            if (error && error.request) {
                console.log(error);
            }
            return Promise.reject(error);
        });

    // RESPONSE
    instance.interceptors.response.use(
        (response) => {
            if (!sessionObservable.customToken) {
                setCustomToken()
            }
            return response;
        },
        async (error) => {
            const originalRequest = error.config;
            if (!error.response) {
                alert("通信状況を確認してください")
                return
            }
            if (error.response.status === 401) {
                const hasSession = !!sessionObservable.cognitoJwt
                // Only act when there IS a session to recover. Without one (e.g. on the login page,
                // or right after logout) just reject and let the app's own routing handle navigation;
                // forcing a redirect here causes a redundant extra reload before reaching /login.
                if (hasSession && !(originalRequest as any)._retry) {
                    (originalRequest as any)._retry = true;
                    if (sessionObservable.cognitoJwt) {
                        // Cognito session → silently refresh the ID token, then retry the request.
                        const result = await refreshCognitoToken()
                        if (result === "ok") {
                            return authorizedFetch(originalRequest)
                        }
                    }
                    // Had a session but refresh failed → token truly expired/invalid → re-login.
                    logout()
                    if (window.location.pathname.indexOf("/login") < 0 && window.location.pathname.indexOf("/password_reset") < 0) {
                        window.location.href = "/login"
                    }
                }
            }
            else if (error.response.status === 412) {
                console.log('need mfa')
                if (window.location.pathname.indexOf("/login") < 0 && window.location.pathname.indexOf("/password_reset")<0) {
                    window.location.href = "/login"
                }
            }
            return Promise.reject(error);
        }
    );
    return instance
})()

// Refresh the Cognito ID token used in the "XXX-Authorize" header.
// The Cognito session (incl. refresh token) is persisted by cognitoLogin() in `userPool`'s storage
// (localStorage). getSession() transparently uses the refresh token when the ID token is expired.
// Returns "ok" (cognitoJwt updated) or "ng" (no session / refresh token expired → caller must re-login).
const refreshCognitoToken = (): Promise<"ok" | "ng"> => {
    return new Promise((resolve) => {
        const cognitoUser = userPool.getCurrentUser()
        if (!cognitoUser) {
            resolve("ng")
            return
        }
        cognitoUser.getSession((err: any, session: any) => {
            if (err || !session || !session.isValid()) {
                resolve("ng")
                return
            }
            sessionObservable.cognitoJwt = session.getIdToken().getJwtToken()
            resolve("ok")
        })
    })
}

const setCustomToken = () => {
    authorizedFetch.get(CUSTOM_TOKEN_URI).then(res => {
        const token = res.data
        sessionObservable.customToken = res.data
        Firebase.instance.auth.signInWithCustomToken(token).then(res => {
        }).catch(function (err) {
            console.log(err)
        });
    }).then(err => {
        console.log(err)
    })
}
